import React, { useState, useEffect } from 'react';
import {
  X,
  Calendar,
  User,
  Edit3,
  Plus,
  Trash2,
  CheckCircle,
  AlertCircle,
  Clock,
  FileText,
  Building,
  Mail,
  Phone,
  RefreshCw,
  Download,
  Copy,
  Check
} from 'lucide-react';

// Helper function to create human-readable descriptions
const getActionDescription = (log) => {
  switch (log.operation_type) {
    case 'INSERT':
      return 'Creación del negocio';
    case 'UPDATE':
      return `Se actualizó el campo: ${log.changed_column || 'información'}`;
    case 'DELETE':
      return 'Eliminación del negocio';
    default:
      return 'Operación del sistema';
  }
};

// Simple user agent parser
const formatUserAgent = (uaString) => {
  if (!uaString) return 'Desconocido';

  let browser = 'Navegador desconocido';
  if (uaString.includes('Chrome')) browser = 'Chrome';
  else if (uaString.includes('Firefox')) browser = 'Firefox';
  else if (uaString.includes('Safari')) browser = 'Safari';
  else if (uaString.includes('Edg')) browser = 'Edge';

  let os = 'SO desconocido';
  if (uaString.includes('Windows')) os = 'Windows';
  else if (uaString.includes('Macintosh')) os = 'macOS';
  else if (uaString.includes('Linux')) os = 'Linux';
  else if (uaString.includes('Android')) os = 'Android';
  else if (uaString.includes('iPhone')) os = 'iOS';

  return `${browser} en ${os}`;
};

const BusinessChangeLogDialog = ({
  isOpen,
  onClose,
  business,
  // Props from parent component (AdminDashboard)
  getChangeLogsByRegistryNumber,
  getChangeLogStats,
  getChangeLogSummary,
  changeLogsLoading,
  changeLogsError,
  clearChangeLogsError
}) => {
  const [changeLogs, setChangeLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [copied, setCopied] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [localLoading, setLocalLoading] = useState(false);

  const itemsPerPage = 20;

  // Load change logs when dialog opens
  useEffect(() => {
    if (isOpen && business && business.business_registry_number) {
      loadChangeLogsAndStats();
    }
  }, [isOpen, business]);

  const loadChangeLogsAndStats = async () => {
    if (!business?.business_registry_number) return;

    setLocalLoading(true);
    try {
      // Clear any previous errors
      clearChangeLogsError();

      // Load both change logs and stats
      const [logsResult, statsResult] = await Promise.all([
        getChangeLogsByRegistryNumber(business.business_registry_number, {
          limit: itemsPerPage,
          offset: 0
        }),
        getChangeLogStats(business.business_registry_number)
      ]);

      if (logsResult) {
        setChangeLogs(logsResult.change_logs || []);
        setTotalCount(logsResult.total_count || 0);
        setHasMore(logsResult.has_more || false);
        setCurrentPage(1);
      }

      if (statsResult) {
        setStats(statsResult.stats);
      }
    } catch (error) {
      console.error('Error loading change logs:', error);
      setChangeLogs([]);
      setStats(null);
    } finally {
      setLocalLoading(false);
    }
  };

  const handleCopyRegistryNumber = async () => {
    try {
      await navigator.clipboard.writeText(business.business_registry_number);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Load more change logs (pagination)
  const loadMoreChangeLogs = async () => {
    if (!business?.business_registry_number || changeLogsLoading) return;

    try {
      const result = await getChangeLogsByRegistryNumber(business.business_registry_number, {
        limit: itemsPerPage,
        offset: currentPage * itemsPerPage
      });

      if (result && result.change_logs) {
        setChangeLogs(prev => [...prev, ...result.change_logs]);
        setCurrentPage(prev => prev + 1);
        setHasMore(result.has_more || false);
      }
    } catch (error) {
      console.error('Error loading more change logs:', error);
    }
  };

  // Export change logs
  const handleExport = async () => {
    if (!business?.business_registry_number) return;

    try {
      // Get all change logs for export
      const result = await getChangeLogsByRegistryNumber(business.business_registry_number, {
        limit: 10000, // Large limit for export
        offset: 0
      });

      if (result && result.change_logs) {
        const exportData = result.change_logs.map(log => ({
          'Log ID': log.log_id,
          'Business ID': log.business_id,
          'Operation': log.operation_type,
          'Changed Column': log.changed_column || 'N/A',
          'Old Value': log.old_value || 'N/A',
          'New Value': log.new_value || 'N/A',
          'Changed At': formatTimestamp(log.changed_at),
          'Changed By': log.changed_by || 'System',
          'IP Address': log.ip_address || 'N/A',
        }));

        // Create CSV content
        const headers = Object.keys(exportData[0] || {});
        const csvContent = [
          headers.join(','),
          ...exportData.map(row =>
            headers.map(header => `"${(row[header] || '').toString().replace(/"/g, '""')}"`).join(',')
          )
        ].join('\n');

        // Download file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `change_logs_${business.business_registry_number}_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error exporting change logs:', error);
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString('es-PR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now - new Date(timestamp)) / (1000 * 60));

    if (diffInMinutes < 60) {
      return `hace ${diffInMinutes} minuto${diffInMinutes !== 1 ? 's' : ''}`;
    } else if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60);
      return `hace ${hours} hora${hours !== 1 ? 's' : ''}`;
    } else {
      const days = Math.floor(diffInMinutes / 1440);
      return `hace ${days} día${days !== 1 ? 's' : ''}`;
    }
  };

  const getOperationConfig = (operationType) => {
    const configs = {
      INSERT: { icon: Plus, color: 'text-green-600', bgColor: 'bg-green-50', label: 'Creado' },
      UPDATE: { icon: Edit3, color: 'text-blue-600', bgColor: 'bg-blue-50', label: 'Actualizado' },
      DELETE: { icon: Trash2, color: 'text-red-600', bgColor: 'bg-red-50', label: 'Eliminado' }
    };
    return configs[operationType] || configs.UPDATE;
  };

  if (!isOpen) return null;

  const isLoading = localLoading || changeLogsLoading;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-orange-100">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-orange-500 rounded-lg">
              <FileText className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Historial de Cambios
              </h2>
              <p className="text-sm text-gray-600 flex items-center gap-1">
                {business?.business_name} • Registro: {business?.business_registry_number}
                <button
                  onClick={handleCopyRegistryNumber}
                  className="p-1 text-gray-400 hover:text-gray-600 hover:bg-orange-200 rounded transition-colors ml-1"
                  title="Copiar número de registro"
                >
                  {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                </button>
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={loadChangeLogsAndStats}
              disabled={isLoading}
              className="p-2 hover:bg-orange-200 rounded-lg transition-colors disabled:opacity-50"
              title="Actualizar"
            >
              <RefreshCw className={`h-4 w-4 text-gray-500 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handleExport}
              disabled={isLoading || changeLogs.length === 0}
              className="p-2 hover:bg-orange-200 rounded-lg transition-colors disabled:opacity-50"
              title="Exportar CSV"
            >
              <Download className="h-4 w-4 text-gray-500" />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-orange-200 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Business Info Summary */}
        <div className="p-6 bg-gray-50 border-b border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center space-x-2">
              <Building className="h-4 w-4 text-orange-500" />
              <span className="text-sm font-medium text-gray-700">
                {business?.business_name}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Mail className="h-4 w-4 text-orange-500" />
              <span className="text-sm text-gray-600">
                {business?.business_email}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Phone className="h-4 w-4 text-orange-500" />
              <span className="text-sm text-gray-600">
                {business?.business_phone}
              </span>
            </div>
          </div>

          {/* Registration Date */}
          {(business?.registration_date || business?.business_created_at) && (
            <div className="mt-4 p-3 bg-white border border-orange-200 rounded-lg flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center space-x-2">
                <div className="p-1.5 bg-orange-100 rounded-md">
                  <Calendar className="h-4 w-4 text-orange-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fecha de Registro
                  </p>
                  <p className="text-sm font-semibold text-gray-900">
                    {formatTimestamp(business?.registration_date || business?.business_created_at)}
                  </p>
                </div>
              </div>
              <span className="text-xs text-gray-500 italic">
                {getTimeAgo(business?.registration_date || business?.business_created_at)}
              </span>
            </div>
          )}

          {/* Stats Summary */}
          {stats && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div className="bg-white rounded-lg p-3">
                  <div className="text-lg font-semibold text-gray-900">{stats.total_logs}</div>
                  <div className="text-xs text-gray-500">Total Logs</div>
                </div>
                <div className="bg-white rounded-lg p-3">
                  <div className="text-lg font-semibold text-blue-600">{stats.update_operations}</div>
                  <div className="text-xs text-gray-500">Actualizaciones</div>
                </div>
                <div className="bg-white rounded-lg p-3">
                  <div className="text-lg font-semibold text-orange-600">{stats.recent_changes}</div>
                  <div className="text-xs text-gray-500">Últimas 24h</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Error Display */}
        {changeLogsError && (
          <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-red-600" />
              <span className="text-red-800 font-medium">Error al cargar historial</span>
            </div>
            <p className="text-red-700 mt-1 text-sm">{changeLogsError}</p>
          </div>
        )}

        {/* Change Logs Content */}
        <div className="overflow-y-auto max-h-[60vh]">
          {isLoading && changeLogs.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Clock className="h-12 w-12 text-orange-500 mx-auto mb-4 animate-pulse" />
                <h3 className="text-lg font-medium text-gray-700 mb-2">
                  Cargando historial...
                </h3>
                <p className="text-gray-500">
                  Obteniendo los registros de cambios del negocio.
                </p>
              </div>
            </div>
          ) : changeLogs.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-700 mb-2">
                Sin historial de cambios
              </h3>
              <p className="text-gray-500">
                No se encontraron registros de cambios para este negocio.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-orange-50 to-orange-100 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Operación
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Campo
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Valor Anterior
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Nuevo Valor
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Usuario
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Fecha y Hora
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      IP / Navegador
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {changeLogs.map((log, index) => {
                    const operationConfig = getOperationConfig(log.operation_type);
                    const Icon = operationConfig.icon;

                    return (
                      <tr
                        key={log.log_id}
                        className={`hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                          }`}
                      >
                        {/* Operation */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <div className={`flex-shrink-0 p-1 rounded-full ${operationConfig.bgColor}`}>
                              <Icon className={`h-3 w-3 ${operationConfig.color}`} />
                            </div>
                            <div>
                              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${operationConfig.bgColor} ${operationConfig.color}`}>
                                {operationConfig.label}
                              </span>
                              <div className="text-xs text-gray-500 mt-1">
                                Log #{log.log_id}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Changed Column */}
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-900">
                            {log.changed_column ? (
                              <span className="font-mono bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                                {log.changed_column}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-xs">N/A</span>
                            )}
                          </div>
                        </td>

                        {/* Old Value */}
                        <td className="px-4 py-3">
                          <div className="text-sm">
                            {log.old_value ? (
                              <div className="font-mono bg-red-50 text-red-700 px-2 py-1 rounded text-xs max-w-xs truncate" title={log.old_value}>
                                {log.old_value}
                              </div>
                            ) : (
                              <span className="text-gray-400 text-xs">N/A</span>
                            )}
                          </div>
                        </td>

                        {/* New Value */}
                        <td className="px-4 py-3">
                          <div className="text-sm">
                            {log.new_value ? (
                              <div className="font-mono bg-green-50 text-green-700 px-2 py-1 rounded text-xs max-w-xs truncate" title={log.new_value}>
                                {log.new_value}
                              </div>
                            ) : (
                              <span className="text-gray-400 text-xs">N/A</span>
                            )}
                          </div>
                        </td>

                        {/* Changed By */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center space-x-1">
                            <User className="h-3 w-3 text-gray-400" />
                            <span className="text-sm text-gray-900">
                              {log.changed_by || 'Sistema'}
                            </span>
                          </div>
                        </td>

                        {/* Timestamp */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {formatTimestamp(log.changed_at)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {getTimeAgo(log.changed_at)}
                          </div>
                        </td>

                        {/* IP and User Agent */}
                        <td className="px-4 py-3">
                          <div className="text-xs text-gray-600">
                            <div className="flex items-center space-x-1 mb-1">
                              <span className="font-mono">
                                {log.ip_address || 'N/A'}
                              </span>
                            </div>
                            <div className="text-gray-500 truncate max-w-32" title={formatUserAgent(log.user_agent)}>
                              {formatUserAgent(log.user_agent)}
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Load More Button */}
              {hasMore && (
                <div className="text-center py-4 bg-gray-50 border-t border-gray-200">
                  <button
                    onClick={loadMoreChangeLogs}
                    disabled={isLoading}
                    className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 transition-colors"
                  >
                    {isLoading ? (
                      <>
                        <Clock className="h-4 w-4 inline mr-2 animate-spin" />
                        Cargando...
                      </>
                    ) : (
                      'Cargar más registros'
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <p className="text-sm text-gray-600">
            {changeLogs.length > 0 ? (
              <>
                Mostrando {changeLogs.length} de {totalCount} registros
                {hasMore && <span className="text-orange-600 ml-1">(hay más disponibles)</span>}
              </>
            ) : (
              'Sin registros disponibles'
            )}
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default BusinessChangeLogDialog;